// Studio data — fills the parity surface against Foyr Manage's CRM
// features (leads, tasks, files, comms). Demo fixtures only; the
// real backend persists into the agent graph (each lead is a
// prov:Activity attached to a foaf:Person; tasks attach to project_iri
// with hgm:Task; files land in s3 with hgm:File subject triples).
// CLAUDE.md §1 — no hardcoded data in components, lives here.

/** Studio roster — the people behind every Task / File row. Real
 *  studios swap this out with their tenant directory on signup. */
export interface StudioMember {
  email: string;
  display_name: string;
  role: "designer" | "pm" | "ops" | "principal";
}
export const STUDIO_ROSTER: readonly StudioMember[] = [
  { email: "maya@hgm",     display_name: "Maya",   role: "designer" },
  { email: "rajiv@hgm",    display_name: "Rajiv",  role: "pm" },
  { email: "khushi@hgm",   display_name: "Khushi", role: "designer" },
  { email: "ananya@hgm",   display_name: "Ananya", role: "ops" },
];
export function studioMemberByRole(role: StudioMember["role"]): StudioMember {
  return STUDIO_ROSTER.find((m) => m.role === role) ?? STUDIO_ROSTER[0]!;
}

/** Cold/qualified leads not yet in PROJECTS. Typed data file (no
 *  literals leaked into code paths). Dates are computed at render
 *  time as `today - aged_days` — never frozen. */
export interface StudioProspect {
  id: string;
  name: string;
  email: string;
  city: string;
  enquiry: string;
  budget_tier: "standard" | "premium" | "luxury";
  stage: "new" | "qualified";
  aged_days: number; // computed today - aged_days at render time
}
export const STUDIO_PROSPECTS: readonly StudioProspect[] = [
  { id: "px-1", name: "Singh family",   email: "p.singh@mail.com",    city: "Delhi",     enquiry: "4BHK · indian-traditional · two elders.",     budget_tier: "premium",  stage: "new",       aged_days:  3 },
  { id: "px-2", name: "Patel siblings", email: "khushi@p.io",         city: "Ahmedabad", enquiry: "Two-villa cluster · scandi · pet-friendly.",  budget_tier: "luxury",   stage: "qualified", aged_days:  6 },
  { id: "px-3", name: "Rao residence",  email: "rao@example.in",      city: "Pune",      enquiry: "3BHK · biophilic · ~₹1.2 Cr · ready by Feb.",  budget_tier: "premium",  stage: "qualified", aged_days:  4 },
  { id: "px-4", name: "Iyer studio",    email: "iyer.creative@in.co", city: "Chennai",   enquiry: "Photographer's studio · loft + lounge.",      budget_tier: "standard", stage: "new",       aged_days:  1 },
];

export type LeadStage = "new" | "qualified" | "proposal" | "won" | "lost";
export type TaskStatus = "todo" | "doing" | "blocked" | "done";

export interface Lead {
  id: string;
  name: string;
  email: string;
  city: string;
  enquiry: string;
  budget_tier: "standard" | "premium" | "luxury";
  stage: LeadStage;
  created: string;
}

export interface Task {
  id: string;
  title: string;
  project_id: string;
  owner: string;
  status: TaskStatus;
  due: string;
  why_stuck?: string;
}

export interface Asset {
  id: string;
  name: string;
  kind: "moodboard" | "render" | "boq" | "contract" | "drawing";
  project_id: string;
  size_kb: number;
  uploaded_by: string;
  uploaded_at: string;
}

export interface CommDraft {
  id: string;
  to: string;
  subject: string;
  preview: string;
  project_id: string;
  drafted_at: string;
  status: "pending_review" | "sent" | "scheduled";
}

export const SEED_LEADS: readonly Lead[] = [
  { id: "l1", name: "Anika & Rohan",  email: "anika.r@gmail.com", city: "Bengaluru",  enquiry: "3BHK · modern · ~₹1.6 Cr, ready Oct 2026.",   budget_tier: "premium",  stage: "qualified", created: "2026-05-08" },
  { id: "l2", name: "Singh family",   email: "p.singh@mail.com",  city: "Delhi",      enquiry: "4BHK · indian-traditional · two elders.",     budget_tier: "premium",  stage: "new",       created: "2026-05-12" },
  { id: "l3", name: "Verma & Co.",    email: "ops@verma.in",      city: "Mumbai",     enquiry: "Restaurant · 1800 sqft · open kitchen.",      budget_tier: "luxury",   stage: "proposal",  created: "2026-04-29" },
  { id: "l4", name: "Patel siblings", email: "khushi@p.io",       city: "Ahmedabad",  enquiry: "Two-villa cluster · scandi · pet-friendly.",  budget_tier: "luxury",   stage: "qualified", created: "2026-05-10" },
];

export const SEED_TASKS: readonly Task[] = [
  { id: "t1", title: "Send Anika the alternate temple-wall plan",   project_id: "p1", owner: "maya@hgm",   status: "todo",    due: "2026-05-17" },
  { id: "t2", title: "Confirm marble vendor for Verma kitchen",      project_id: "p3", owner: "rajiv@hgm",  status: "blocked", due: "2026-05-19", why_stuck: "Awaiting vendor sample." },
  { id: "t3", title: "Render kids room — Singh family",              project_id: "p2", owner: "maya@hgm",   status: "doing",   due: "2026-05-22" },
  { id: "t4", title: "Approve final BOQ — Patel duplex",             project_id: "p4", owner: "khushi@p.io", status: "todo",   due: "2026-05-20" },
];

export const SEED_ASSETS: readonly Asset[] = [
  { id: "a1", name: "moodboard_anika_v3.fig",         kind: "moodboard", project_id: "p1", size_kb: 12_400, uploaded_by: "maya@hgm",  uploaded_at: "2026-05-09" },
  { id: "a2", name: "BOQ_master_bedroom_v2.xlsx",     kind: "boq",       project_id: "p1", size_kb:  68,    uploaded_by: "maya@hgm",  uploaded_at: "2026-05-11" },
  { id: "a3", name: "render_living_warm.png",         kind: "render",    project_id: "p1", size_kb:  4_800, uploaded_by: "agent",     uploaded_at: "2026-05-12" },
  { id: "a4", name: "contract_singh_v1.pdf",          kind: "contract",  project_id: "p2", size_kb:   320,  uploaded_by: "rajiv@hgm", uploaded_at: "2026-05-13" },
  { id: "a5", name: "verma_kitchen_drawing.dxf",      kind: "drawing",   project_id: "p3", size_kb: 1_200,  uploaded_by: "agent",     uploaded_at: "2026-05-13" },
];

export const SEED_COMMS: readonly CommDraft[] = [
  {
    id: "c1",
    to: "anika.r@gmail.com",
    subject: "Re: temple-wall position — three options ready",
    preview: "Hi Anika, sharing three alternatives that respect both the spatial flow and the rule we discussed.",
    project_id: "p1",
    drafted_at: "2026-05-14",
    status: "pending_review",
  },
  {
    id: "c2",
    to: "p.singh@mail.com",
    subject: "Site visit · Saturday 11 AM works?",
    preview: "Hello — proposing a 45-min site visit Saturday 11 AM with Maya. Confirm or pick a slot.",
    project_id: "p2",
    drafted_at: "2026-05-14",
    status: "pending_review",
  },
];
